import React, {
  useState,
} from 'react';
import noop from 'lodash/noop';
import Dialog from 'pages/Dashboard/components/Dialog';
import Skeleton from 'core/components/Skeleton';
import DateRangePicker from 'core/components/DateTimePicker/DateRangePicker';
import {
  getVitalTrends,
  getDefaultVitalsDateRange,
  hasVitalTrendData,
  vitalMetrics,
  VITALS_EMPTY_STATE_TEXT,
} from 'core/vitals/utils';
import VitalsTrendCard from 'views/EMR/components/VitalsTrendsDialog/TrendCard';
import {
  useVitalsHistory,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/hooks';

export type VitalsTrendsDialogParams = {
  patientId: number;
};

type Props = VitalsTrendsDialogParams & {
  onClose: () => void;
};

export default function VitalsTrendsDialog({
  patientId,
  onClose,
}: Props) {
  const [dateRange, setDateRange] = useState(getDefaultVitalsDateRange);
  const {
    data,
    isError,
    isLoading,
  } = useVitalsHistory(patientId, dateRange);

  const cards = getVitalTrends(data ?? []);
  const hasTrendData = hasVitalTrendData(cards.map(({ points }) => points));

  return (
    <Dialog
      open
      title={<h2 className="text-lg font-semibold">Vitals Trends</h2>}
      headerActions={(
        <div className="ml-auto">
          <DateRangePicker
            value={dateRange}
            onChange={(nextDateRange) => {
              if (nextDateRange) {
                setDateRange(nextDateRange);
              }
            }}
          />
        </div>
      )}
      onClose={onClose}
      onConfirm={noop}
      className="!w-fit !max-w-[calc(100vw-3rem)] !space-y-3"
      closeBtnClasses="!right-0 !top-0"
      titleClassName="!justify-start !items-center !gap-3"
      maxWidth={false}
    >
      <div className="flex flex-col gap-6">
        {isLoading ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[24rem_24rem]">
            {vitalMetrics.map((metric) => (
              <div
                key={metric.id}
                className={`rounded-md border border-gray-200/80 bg-white p-4 ${metric.series.length > 1 ? 'md:col-span-2' : ''}`}
              >
                <Skeleton className="mb-3 h-4 !w-28" />
                <Skeleton className="mb-4 h-8 !w-24" />
                <Skeleton className="h-32 !w-full" />
                <Skeleton className="mt-3 h-4 !w-40" />
              </div>
            ))}
          </div>
        ) : !isError && (
          hasTrendData ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-[24rem_24rem]">
              {cards.map(({ metric, points }) => (
                <VitalsTrendCard
                  key={metric.id}
                  metric={metric}
                  points={points}
                />
              ))}
            </div>
          ) : (
            <div className="py-12 text-center text-sm text-gray-500">
              {VITALS_EMPTY_STATE_TEXT.noVitalsForDateRange}
            </div>
          )
        )}
        {isError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-8 text-center text-sm text-red-700">
            Failed to load vitals history. Please try again.
          </div>
        )}
      </div>
    </Dialog>
  );
}
